import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiChevronDown, 
  FiChevronUp, 
  FiCode, 
  FiDatabase, 
  FiZap, 
  FiGithub, 
  FiServer, 
  FiTrendingUp, 
  FiMapPin,
  FiImage
} from 'react-icons/fi';

const Experience = () => {
  const [openFirst, setOpenFirst] = useState(false);
  const [openSecond, setOpenSecond] = useState(false);

  const pythonicLabProjects = [
    {
      icon: <FiCode className="w-5 h-5" />,
      title: 'Quote Generator',
      description: 'API-based application with modern GUI',
      github: 'https://github.com/atharv-patki/python_mini_projects.git'
    },
    {
      icon: <FiDatabase className="w-5 h-5" />,
      title: 'Student Marks Manager',
      description: 'Complete CRUD operations with data visualization',
      github: 'https://github.com/atharv-patki/python_mini_projects.git'
    },
    {
      icon: <FiZap className="w-5 h-5" />,
      title: 'QR Code Generator',
      description: 'Dynamic QR code generation with custom styling',
      github: 'https://github.com/atharv-patki/python_mini_projects.git'
    },
    {
      icon: <FiCode className="w-5 h-5" />,
      title: 'Expense Tracker',
      description: 'Pie chart analytics with PDF export functionality',
      github: 'https://github.com/atharv-patki/python_mini_projects.git'
    }
  ];

  const pinnacleProjects = [
    {
      icon: <FiServer className="w-5 h-5" />,
      title: 'calculator.py',
      description: 'Basic calculator application to perform arithmetic operations with a clean CLI interface.',
      github: 'https://github.com/atharv-patki/Pinnacle-Lab/blob/main/calculator.py' // update to exact repo if available
    },
    {
      icon: <FiTrendingUp className="w-5 h-5" />,
      title: 'weather.py',
      description: 'Weather script that fetches and displays real-time weather details for any city using APIs.',
      github: 'https://github.com/atharv-patki/Pinnacle-Lab/blob/main/weather.py' // update to exact repo if available
    }
  ];

  return (
    <section id="experience" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Work Experience
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            My journey as a Python Developer and data-focused intern, building applications and working with modern technologies.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto space-y-8">

          {/* Pinnacle Lab - Completed, blue theme */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 shadow-lg"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-2xl font-bold text-gray-900">
                    Python Developer Intern
                  </h3>
                  <span className="px-3 py-1 bg-blue-500 text-white text-sm font-medium rounded-full">
                    Completed
                  </span>
                </div>
                <p className="text-blue-700 font-medium mb-1">Pinnacle Lab</p>
                <p className="text-blue-600 font-medium mb-2">2025</p>
                <div className="flex items-center text-gray-600 mb-4">
                  <FiMapPin className="w-4 h-4 mr-2" />
                  <span className="text-sm">Remote</span>
                </div>
                <p className="text-gray-600 mb-6">
                  Completed a Python development internship focused on practical mini-projects. 
                  Built command-line utilities that improved core Python skills, problem solving, 
                  and basics of working with external APIs and data.
                </p>
                
                <div className="flex flex-wrap gap-2 mb-6">
                  {['Python', 'CLI Applications', 'APIs', 'Problem Solving'].map((tech) => (
                    <span 
                      key={tech} 
                      className="px-3 py-1 bg-blue-500/10 text-blue-700 rounded-full text-sm font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                {/* certificate button removed from body - now will be in header */}
              </div>

              {/* Certificate Button (header) */}
              <motion.button
                onClick={() => window.open('/images/certifications/pinnacle-certificate.png', '_blank')}
                whileHover={{ scale: 1.02, y: -1 }}
                className="ml-4 mr-2 flex-shrink-0 cursor-pointer select-none text-blue-600 hover:underline text-sm font-medium flex items-center px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-md"
                aria-label="View Pinnacle Lab certificate"
              >
                <FiImage className="w-4 h-4 mr-0 sm:mr-1" />
                <span className="hidden sm:inline ml-2">View certificate</span>
              </motion.button>

              {/* View Details Button */}
              <motion.span
                onClick={() => setOpenSecond(!openSecond)}
                whileHover={{ scale: 1.02, y: -1 }}
                className="ml-2 flex-shrink-0 cursor-pointer select-none text-blue-600 hover:underline text-sm font-medium flex items-center px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-md"
              >
                {openSecond ? (
                  <>
                    <span className="hidden sm:inline">Hide details</span>
                    <FiChevronUp className="ml-1 sm:ml-1" />
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">View details</span>
                    <FiChevronDown className="ml-1 sm:ml-1" />
                  </>
                )}
              </motion.span>
            </div>

            {/* Collapsible Projects Section */}
            <AnimatePresence>
              {openSecond && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.4 }}
                  className="mt-8 overflow-hidden"
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Tasks Completed:
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    {pinnacleProjects.map((project, index) => (
                      <motion.a
                        key={project.title}
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        className="group bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md hover:border-blue-500/50 transition-all duration-300"
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center text-blue-600 group-hover:bg-blue-500/20 transition-colors duration-200">
                            {project.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h5 className="text-md font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
                                {project.title}
                              </h5>
                              <div className="flex items-center ml-2 text-xs text-blue-600 hover:underline">
                                <FiGithub className="w-4 h-4 mr-1" />
                                View on GitHub
                              </div>
                            </div>
                            <p className="text-gray-600 text-sm">
                              {project.description}
                            </p>
                          </div>
                        </div>
                      </motion.a>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Pythonic Lab - Completed, original blue theme */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 shadow-lg"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-2xl font-bold text-gray-900">
                    Python Developer Intern
                  </h3>
                  <span className="px-3 py-1 bg-blue-500 text-white text-sm font-medium rounded-full">
                    Completed
                  </span>
                </div>
                <p className="text-primary font-medium mb-1">Pythonic Lab</p>
                <p className="text-primary font-medium mb-2">Sep 2024 - Oct 2024</p>
                <div className="flex items-center text-gray-600 mb-4">
                  <FiMapPin className="w-4 h-4 mr-2" />
                  <span className="text-sm">Borivali, Maharashtra, India • Remote</span>
                </div>
                <p className="text-gray-600 mb-6">
                  Developed multiple GUI applications using Tkinter, integrated external APIs, 
                  and implemented data visualization with matplotlib. Enhanced user experience 
                  through intuitive interface design and efficient backend processing.
                </p>
                
                <div className="flex flex-wrap gap-2 mb-6">
                  {['Python', 'Tkinter', 'Matplotlib', 'ReportLab', 'QRCode', 'APIs'].map((tech) => (
                    <span key={tech} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                      {tech}
                    </span>
                  ))}
                </div>
                {/* certificate button removed from body - now will be in header */}
              </div>

              {/* Certificate Button (header) */}
              <motion.button
                onClick={() => window.open('/images/certifications/pythonic-certificate.png', '_blank')}
                whileHover={{ scale: 1.02, y: -1 }}
                className="ml-4 mr-2 flex-shrink-0 cursor-pointer select-none text-primary hover:underline text-sm font-medium flex items-center px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-md"
                aria-label="View Pythonic Lab certificate"
              >
                <FiImage className="w-4 h-4 mr-0 sm:mr-1" />
                <span className="hidden sm:inline ml-2">View certificate</span>
              </motion.button>

              {/* View Details Button */}
              <motion.span
                onClick={() => setOpenFirst(!openFirst)}
                whileHover={{ scale: 1.02, y: -1 }}
                className="ml-2 flex-shrink-0 cursor-pointer select-none text-primary hover:underline text-sm font-medium flex items-center px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-md"
              >
                {openFirst ? (
                  <>
                    <span className="hidden sm:inline">Hide details</span>
                    <FiChevronUp className="ml-1 sm:ml-1" />
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">View details</span>
                    <FiChevronDown className="ml-1 sm:ml-1" />
                  </>
                )}
              </motion.span>
            </div>

            {/* Collapsible Projects Section */}
            <AnimatePresence>
              {openFirst && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.4 }}
                  className="mt-8 overflow-hidden"
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Projects Developed:
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    {pythonicLabProjects.map((project, index) => (
                      <motion.a
                        key={project.title}
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        className="group bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md hover:border-primary/50 transition-all duration-300"
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary group-hover:bg-primary/20 transition-colors duration-200">
                            {project.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h5 className="text-md font-semibold text-gray-900 group-hover:text-primary transition-colors duration-200">
                                {project.title}
                              </h5>
                              <div className="flex items-center ml-2 text-xs text-primary hover:underline">
                                <FiGithub className="w-4 h-4 mr-1" />
                                View on GitHub
                              </div>
                            </div>
                            <p className="text-gray-600 text-sm">
                              {project.description}
                            </p>
                          </div>
                        </div>
                      </motion.a>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default Experience;
